import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import userManager from "../authorization/authService";
import { AuthenticationState } from "./AuthenticationState";
import { RootState } from "../../app/store";
import { UserAuth } from "./UserAuth";
import { serializeOidcUser } from "./serializeUser";
import { registerUserAsync, loginUserAsync } from "./authenticationApi";
import { getUserInfoFromToken } from "./JwtDecode";
import { clearAllCookies } from "../extensions/clearAllCookies";
import { BYPASS_AUTH, LOCAL_USER_EMAIL, LOCAL_USER_NAME, LOCAL_USER_ROLE } from "../../config";
const initialState: AuthenticationState = {
  initializationStatus: "not-initialized",
  userAuth: {
    id_token: "",
    session_state: "",
    access_token: "",
    token_type: "",
    scope: "",
    refresh_token: "",
    profile: {
      s_hash: "",
      sid: "",
      sub: "",
      auth_time: 0,
      idp: "",
      amr: [],
      emailaddress: "",
      name: "",
      role: "",
      picture: "",
    },
    expires_at: 0,
  },
  status: "idle",
  isAuthenticated: false,
};

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    updateAuthenticationState: (state, action) => {
      setUserAuthInfoByAuthManager(action.payload, state);
    },
    updateToken: (state, action: PayloadAction<string>) => {
      if (state.userAuth) {
        state.userAuth.access_token = action.payload;
      }
    },
    clearAuthenticationData: (state) => {
      // Explicitly remove access token from localStorage
      localStorage.removeItem("access_token");

      resetUserAuth(state);
      state.isAuthenticated = false;
      state.status = "idle";
      state.initializationStatus = "initialized";
    },
    setAuthenticationError: (state, action) => {
      resetUserAuth(state);
      state.status = "failed";
      state.isAuthenticated = false;
      state.initializationStatus = "initialized";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadInitialAuthenticationStateAsync.pending, (state) => {
        state.initializationStatus = "initializing";
        state.status = "loading";
      })
      .addCase(
        loadInitialAuthenticationStateAsync.fulfilled,
        (state, action) => {
          if (action.payload) {
            const isTokenAuth =
              !action.payload.id_token && action.payload.access_token;

            if (typeof isTokenAuth !== "undefined" && isTokenAuth) {
              state.userAuth.access_token = action.payload.access_token;

              if (action.payload.profile) {
                state.userAuth.profile.emailaddress =
                  action.payload.profile.emailaddress || "";
                state.userAuth.profile.role = action.payload.profile.role || "";
                state.userAuth.profile.sub = action.payload.profile.sub || "";
              }

              state.isAuthenticated = true;
            } else {
              const decodedToken = getUserInfoFromToken(
                action.payload.access_token
              );
              state.userAuth.profile.emailaddress = decodedToken?.email || "";
              state.userAuth.profile.role = decodedToken?.role || "";

              setUserAuthInfoByAuthManager(action.payload, state);
            }

            state.initializationStatus = "initialized";
            state.status = "idle";
          } else {
            resetUserAuth(state);
            state.isAuthenticated = false;
            state.initializationStatus = "initialized";
            state.status = "idle";
          }
        }
      )
      .addCase(loadInitialAuthenticationStateAsync.rejected, (state) => {
        state.status = "failed";
        state.initializationStatus = "initialized";
      })
      .addCase(logoutAsync.pending, (state) => {
        state.status = "loading";
        state.initializationStatus = "initializing";
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.status = "failed";
        state.isAuthenticated = false;
        state.initializationStatus = "initialized";
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        localStorage.removeItem("access_token");

        state.isAuthenticated = false;
        state.initializationStatus = "initialized";
        state.status = "idle";
      })
      .addCase(registerUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(registerUserAsync.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(registerUserAsync.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(loginUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginUserAsync.fulfilled, (state, action) => {
        const { token, userId, email } = action.payload;
        state.status = "idle";
        state.userAuth.access_token = token;
        state.userAuth.profile.sub = userId;
        state.userAuth.profile.emailaddress = email;
        state.isAuthenticated = true;

        localStorage.setItem("access_token", token);
      })
      .addCase(loginUserAsync.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

const resetUserAuth = (state: AuthenticationState) => {
  // Remove access token from localStorage
  localStorage.removeItem("access_token");

  state.userAuth = {
    id_token: "",
    session_state: "",
    access_token: "",
    token_type: "",
    scope: "",
    refresh_token: "",
    profile: {
      s_hash: "",
      sid: "",
      sub: "",
      auth_time: 0,
      idp: "",
      amr: [],
      role: "",
      emailaddress: "",
      name: "",
      picture: "",
    },
    expires_at: 0,
  };
};

const setUserAuthInfoByAuthManager = (
  authUser: UserAuth | null,
  state: AuthenticationState
) => {
  if (typeof authUser !== "undefined" && authUser !== null) {
    state.userAuth = {
      id_token: authUser.id_token || "",
      session_state: authUser.session_state || "",
      access_token: authUser.access_token || "",
      token_type: authUser.token_type || "",
      scope: authUser.scope || "",
      refresh_token: authUser.refresh_token || "",
      profile: {
        s_hash: authUser.profile?.s_hash || "",
        sid: authUser.profile?.sid || "",
        sub: authUser.profile?.sub || "",
        auth_time: authUser.profile?.auth_time || 0,
        idp: authUser.profile?.idp || "",
        amr: authUser.profile?.amr || [],
        role: authUser.profile?.role || "",
        emailaddress: authUser.profile?.emailaddress || "",
        name: authUser.profile?.name || "",
        picture: authUser.profile?.picture || "",
      },
      expires_at: authUser.expires_at || 0,
    };
    state.isAuthenticated = true;
    state.initializationStatus = "initialized";
    state.status = "idle";
  } else {
    state.isAuthenticated = false;
    state.initializationStatus = "initialized";
    state.status = "idle";
    state.userAuth = {
      id_token: "",
      session_state: "",
      access_token: "",
      token_type: "",
      scope: "",
      refresh_token: "",
      profile: {
        s_hash: "",
        sid: "",
        sub: "",
        auth_time: 0,
        idp: "",
        amr: [],
        role: "",
        emailaddress: "",
        name: "",
        picture: "",
      },
      expires_at: 0,
    };
  }
};

export const logoutAsync = createAsyncThunk(
  "authentication/logout",
  async (_, { dispatch, getState }) => {
    localStorage.setItem(
      "redirectUrl",
      window.location.pathname + window.location.search
    );

    const state = getState() as RootState;
    const hasOidcAuth = state.authentication.userAuth.id_token !== "";

    localStorage.removeItem("access_token");

    clearAllCookies();

    if (hasOidcAuth) {
      await userManager.clearStaleState();

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("oidc")) {
          localStorage.removeItem(key);
        }
      });

      const user = await userManager.getUser();
      if (user) {
        await userManager.signoutRedirect({
          id_token_hint: user.id_token,
          extraQueryParams: { prompt: "logout" },
        });
        await userManager.signoutRedirectCallback();
      }
    }

    dispatch(clearAuthenticationData());
  }
);

export const loadInitialAuthenticationStateAsync = createAsyncThunk(
  "authentication/loadInitialAuthenticationState",
  async () => {
    try {
      // Check if authentication bypass is enabled for local development
      if (BYPASS_AUTH) {
        return {
          access_token: "bypass-token",
          profile: {
            emailaddress: LOCAL_USER_EMAIL,
            role: LOCAL_USER_ROLE,
            name: LOCAL_USER_NAME,
            sub: `local-dev-${Date.now()}`,
          },
        } as UserAuth;
      }

      const storedToken = localStorage.getItem("access_token");

      if (storedToken) {
        const decodedToken = getUserInfoFromToken(storedToken);
        if (decodedToken) {
          return {
            access_token: storedToken,
            profile: {
              emailaddress: decodedToken.email || "",
              role: decodedToken.role || "",
              sub: `local-auth-${Date.now()}`,
            },
          } as UserAuth;
        }
      }

      let user = await userManager.getUser();
      
      if (user && user.expired) {
        try {
          user = await userManager.signinSilent();
        } catch (refreshError) {
          console.error("❌ Silent refresh failed, user needs to login again:", refreshError);
          await userManager.removeUser();
          return null;
        }
      }
      
      if (typeof user !== "undefined" && user !== null) {
        const serializableUser = serializeOidcUser(user);
        return serializableUser;
      }

      return null;
    } catch (error) {
      console.error("Authentication initialization error:", error);
      return null;
    }
  }
);

export const selectAuthInitializationStatus = (state: RootState) =>
  state.authentication.initializationStatus;

export const selectIsAuthenticated = (state: RootState) =>
  state.authentication.isAuthenticated;

export const selectAuthenticatedRole = (state: RootState) =>
  state.authentication.userAuth.profile.role;

export const selectUserProfile = (state: RootState) =>
  state.authentication.userAuth.profile;

export const selectAuthMethod = (state: RootState) => {
  if (!state.authentication.isAuthenticated) return "none";
  return state.authentication.userAuth.id_token ? "oidc" : "token";
};

export const {
  clearAuthenticationData,
  updateAuthenticationState,
  setAuthenticationError,
  updateToken,
} = authenticationSlice.actions;

export default authenticationSlice.reducer;
